import { BedDouble, CalendarCheck, Home, Sparkles } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { ApartmentCard } from "../components/ApartmentCard";
import { SEO } from "../components/SEO";
import { Reveal } from "../components/motion/Reveal";
import { Stagger } from "../components/motion/Stagger";
import { apartmentCategories, apartments } from "../data/apartments";
import { siteInfo } from "../data/site";
import type { ApartmentCategory } from "../types";

export function Apartments() {
  const [category, setCategory] = useState<ApartmentCategory | "All">("All");
  const bookableApartments = apartments.filter((apartment) => apartment.status === "available");
  const inquiryApartments = apartments.filter((apartment) => apartment.status !== "available");
  const visibleBookable = category === "All" ? bookableApartments : bookableApartments.filter((apartment) => apartment.category === category);
  const visibleInquiry = category === "All" ? inquiryApartments : inquiryApartments.filter((apartment) => apartment.category === category);
  const hasResults = visibleBookable.length > 0 || visibleInquiry.length > 0;
  const bookableBeds = bookableApartments.reduce((total, apartment) => total + (apartment.bedrooms ?? 0), 0);

  return (
    <>
      <SEO
        title="Apartments | The Deposit Seed House"
        description="Browse furnished studio, one-bedroom, two-bedroom, and three-bedroom apartments at The Deposit Seed House in Deposit, NY."
        image={siteInfo.exteriorImage}
        canonicalPath="/apartments"
      />
      <section className="page-hero page-hero--apartments">
        <Reveal>
          <p className="eyebrow">Apartments</p>
          <h1>Choose a furnished stay that fits your trip.</h1>
          <p>Each unit is furnished with linens, towels, cookware, tableware, a smart TV, and Wi-Fi.</p>
          <div className="page-hero__stats" aria-label="Apartment summary">
            <span>
              <Home size={17} aria-hidden="true" />
              {bookableApartments.length} bookable stays
            </span>
            <span>
              <BedDouble size={17} aria-hidden="true" />
              {bookableBeds}+ bedrooms
            </span>
            <span>
              <CalendarCheck size={17} aria-hidden="true" />
              Request now, confirm direct
            </span>
          </div>
          <div className="page-hero__actions">
            <a className="button button--primary" href="#available-apartments-title">
              Browse available stays
            </a>
            <Link className="button button--light" to="/contact">
              Ask a question
            </Link>
          </div>
        </Reveal>
      </section>
      <section className="section section--muted apartments-index-section">
        <div className="apartments-index">
          <aside className="apartments-index__filters">
            <div className="apartments-index__filter-heading">
              <Sparkles size={17} aria-hidden="true" />
              Filter stays
            </div>
            <div className="filter-bar" aria-label="Apartment filters">
              {apartmentCategories.map((item) => (
                <button key={item} type="button" className={item === category ? "filter-button active" : "filter-button"} onClick={() => setCategory(item)}>
                  {item}
                </button>
              ))}
            </div>
          </aside>
          <div className="apartments-index__content">
            {visibleBookable.length > 0 && (
              <section className="apartments-index__section" aria-labelledby="available-apartments-title">
                <Reveal className="apartments-index__section-heading">
                  <p className="eyebrow">Available stays</p>
                  <h2 id="available-apartments-title">Ready for booking requests</h2>
                </Reveal>
                <Stagger className="card-grid apartments-index__grid" key={`bookable-${category}`}>
                  {visibleBookable.map((apartment) => (
                    <ApartmentCard key={apartment.slug} apartment={apartment} motionItem />
                  ))}
                </Stagger>
              </section>
            )}
            {visibleInquiry.length > 0 && (
              <section className="apartments-index__section apartments-index__section--inquiry" aria-labelledby="inquiry-apartments-title">
                <Reveal className="apartments-index__section-heading">
                  <p className="eyebrow">Inquiries</p>
                  <h2 id="inquiry-apartments-title">Future and special-use spaces</h2>
                  <p>These spaces are not accepting booking requests yet. Contact us if you would like timing updates or details.</p>
                </Reveal>
                <Stagger className="card-grid apartments-index__grid" key={`inquiry-${category}`}>
                  {visibleInquiry.map((apartment) => (
                    <ApartmentCard key={apartment.slug} apartment={apartment} motionItem />
                  ))}
                </Stagger>
              </section>
            )}
            {!hasResults && <p className="apartments-index__empty">No spaces match that filter.</p>}
          </div>
        </div>
      </section>
    </>
  );
}
